import { useMutation } from '@apollo/client';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { InsertRiskAssessmentResultConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetLatestRiskAssessmentResultConfigDocument,
  GetRiskAssessmentResultConfigByIdsDocument,
  InsertRiskAssessmentResultConfigDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useInsertRiskAssessmentResultConfigTRPC } from './useInsertRiskAssessmentResultConfigTRPC';

export const useInsertRiskAssessmentResultConfig = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [mutateGraphQL, graphqlState] = useMutation(
    InsertRiskAssessmentResultConfigDocument,
    {
      refetchQueries: [
        {
          query: GetLatestRiskAssessmentResultConfigDocument,
          variables: { Type: 'risk_assessment' },
        },
        { query: GetRiskAssessmentResultConfigByIdsDocument },
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertRiskAssessmentResultConfigTRPC();

  const mutate = async (variables: {
    variables: { Config: RiskAssessmentResultConfig; Type: string };
  }): Promise<InsertRiskAssessmentResultConfigMutation> => {
    if (trpcEnabled) {
      return trpcMutation.mutate(variables);
    }

    const result = await mutateGraphQL(variables);
    if (!result.data) {
      throw new Error('Failed to insert risk assessment result config');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      mutate,
      loading: trpcMutation.loading,
    };
  }

  return {
    mutate,
    loading: graphqlState.loading,
  };
};
